import { useCallback, useEffect, useSyncExternalStore } from 'react';
import {
  ApproverType,
  ContentWorkflow,
  PublisherReviewStatus,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';

import { displayAuthorInfo } from '../../api/UtilsApi';
import { useCanActAsBarRaiser } from '../v2-hooks/useCanActAsBarRaiser';
import { useCoAuthors } from '../v2-hooks/useCoAuthors';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useOptionalBarRaiserEvaluationService } from '../../contexts/BarRaiserEvaluationServiceContext';
import { useCurrentUser } from '../../contexts/UserContext';
import { useLoadedDocument } from '../v2-hooks/useLoadedDocument';
import { log } from '../../loggers/Logger';
import { isLockedByOtherUser, isLockedBySystem } from '../../model/LoadedDocument';

/**
 * Which bar raiser caution banner to show on published content:
 * `not-reviewed` while review is still pending, `rejected` after a bar
 * raiser sends the content back requesting updates.
 */
export type BarRaiserWarning = 'not-reviewed' | 'rejected';

/** Read-only presentation state for the banners shown above the content view tabs. */
export interface ContentStatusBannersViewModel {
  /** Display name of the user holding the edit lock, when held by someone other than the current user; `undefined` otherwise. */
  readonly lockedByUserName: string | undefined;
  /** True when an automated system process holds the content lock. */
  readonly isSystemLocked: boolean;
  /** True when the content is being finalized for publication. */
  readonly isFinalizing: boolean;
  /** True when a bar raiser should be told the author is editing (approvals and new comments are blocked). */
  readonly isAuthorEditingForBarRaiser: boolean;
  /** The bar raiser caution banner to show, or `undefined` when a bar raiser approved it or there is no review info. */
  readonly barRaiserWarning: BarRaiserWarning | undefined;
  /** True when the user has just been redirected here after their publish succeeded, until they dismiss the notice. */
  readonly justPublished: boolean;
  readonly dismissJustPublished: () => void;
}

/** Resolves the display name of the lock holder from the workflow owner or the co-authors. */
function resolveLockHolderName(
  workflow: ContentWorkflow,
  coAuthors: readonly {
    readonly userId: string;
    readonly userName: string;
    readonly organizationName?: string;
  }[],
): string {
  const acquiredBy = workflow.lock?.acquiredBy;
  if (workflow.ownerProfile && acquiredBy === workflow.ownerProfile.userId) {
    return displayAuthorInfo(workflow.ownerProfile.name, workflow.ownerProfile.organizationName);
  }
  const lockHolder = coAuthors.find((c) => c.userId === acquiredBy);
  return lockHolder ? displayAuthorInfo(lockHolder.userName, lockHolder.organizationName) : '';
}

/**
 * Presentation state for the status banners on the read-only view page:
 * a content-locked notice, a finalizing notice, and (for bar raisers) an
 * author-is-editing notice. Bar raisers refresh the workflow once on mount so
 * the editing notice reflects the author's lock at the time the page opens.
 */
export function useContentStatusBannersViewModel(): ContentStatusBannersViewModel | undefined {
  const service = useContentEditorService();
  const { userId } = useCurrentUser();
  const canActAsBarRaiser = useCanActAsBarRaiser();
  const doc = useLoadedDocument();
  const coAuthors = useCoAuthors();
  const barRaiserService = useOptionalBarRaiserEvaluationService();

  // When the bar raiser service's just-published marker changes, then re-render so the
  // confirmation notice appears and dismissal hides it. Without the service there is nothing to bind.
  const subscribeToBarRaiser = useCallback(
    (onStoreChange: () => void) => barRaiserService?.on('changed', onStoreChange) ?? (() => {}),
    [barRaiserService],
  );
  const lastPublishedContentId = useSyncExternalStore(
    subscribeToBarRaiser,
    () => barRaiserService?.lastPublishedContentId,
  );

  // When the user dismisses the just-published notice, clear the marker so it stops showing.
  const dismissJustPublished = useCallback(
    () => barRaiserService?.dismissLastPublished(),
    [barRaiserService],
  );

  // When viewing as a bar raiser, refresh the workflow once so the author-editing
  // notice reflects the current lock state as soon as the page opens.
  useEffect(() => {
    if (!canActAsBarRaiser) return;
    service.refreshWorkflow().catch((err) => {
      log.warn({ error: err }, 'Failed to refresh workflow for author edits');
    });
  }, [service, canActAsBarRaiser]);

  // A parent-teardown render can transiently clear the document before this
  // view unmounts; return undefined so the view renders null instead of throwing.
  if (!doc) {
    return undefined;
  }

  if (doc.kind === 'finalizing') {
    throw new Error('ContentStatusBannersViewModel does not support finalizing mode yet');
  }

  // Only the published version of the just-published content confirms the publish; a draft never does.
  const justPublished = doc.kind === 'publication' && lastPublishedContentId === doc.contentId;

  if (doc.kind === 'publication') {
    const reviewStatus = doc.publisherReviewInfo?.reviewStatus;
    const approverType = doc.publisherReviewInfo?.approverType;

    // Mirrors the backend getPublisherReviewAlertMessage mapping:
    //  - REJECTED  -> "requested updates" caution.
    //  - PENDING   -> "not reviewed" caution (awaiting a bar raiser decision).
    //  - APPROVED  -> caution clears only when a BAR_RAISER approved; an APPROVED
    //                 status from any other approver (e.g. ADMIN) still shows the
    //                 "not reviewed" caution.
    //  - missing publisherReviewInfo / any other status -> no caution.
    let barRaiserWarning: BarRaiserWarning | undefined;
    if (reviewStatus === PublisherReviewStatus.REJECTED) {
      barRaiserWarning = 'rejected';
    } else if (reviewStatus === PublisherReviewStatus.PENDING) {
      barRaiserWarning = 'not-reviewed';
    } else if (reviewStatus === PublisherReviewStatus.APPROVED) {
      barRaiserWarning = approverType === ApproverType.BAR_RAISER ? undefined : 'not-reviewed';
    } else {
      barRaiserWarning = undefined;
    }

    return {
      lockedByUserName: undefined,
      isSystemLocked: false,
      isFinalizing: false,
      isAuthorEditingForBarRaiser: false,
      barRaiserWarning,
      justPublished,
      dismissJustPublished,
    };
  }

  // Draft mode — workflow is always present.
  const workflow = doc.workflow;
  const isLockedByOther = isLockedByOtherUser(workflow, userId);

  const isAuthorEditingForBarRaiser =
    canActAsBarRaiser && workflow.draftState === WorkflowState.PUBLISHER_REVIEW && isLockedByOther;

  return {
    lockedByUserName:
      isLockedByOther && !isAuthorEditingForBarRaiser
        ? resolveLockHolderName(workflow, coAuthors)
        : undefined,
    isSystemLocked: isLockedBySystem(workflow),
    isFinalizing: workflow.publicationState === WorkflowState.FINALIZING,
    isAuthorEditingForBarRaiser,
    barRaiserWarning: undefined,
    justPublished,
    dismissJustPublished,
  };
}
